import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";
import { fetchAuthSession } from "aws-amplify/auth";
import aws4 from "aws4-tiny";
import { apiConfig } from "../../configs/api";
import {
  cognitoIdentityPoolId,
  userPoolId,
  region,
} from "../../configs/cognito";

interface CachedCredentials {
  credentials: any;
  expiration: Date;
}

interface TokenInfo {
  token: string;
  expiration: Date;
}

/**
 * Authentication Service - Clean Implementation
 *
 * Responsibilities:
 * 1. Store and manage ID token
 * 2. Schedule automatic token refresh
 * 3. Provide AWS credentials for API calls
 * 4. Handle network errors gracefully
 *
 * Key Principles:
 * - Single source of truth for token state
 * - Clear separation of concerns
 * - Graceful error handling
 * - No circular dependencies
 */
export class AuthenticationService {
  private static instance: AuthenticationService;

  // Token state
  private static idToken: string | undefined;
  private static tokenInfo: TokenInfo | null = null;
  private static tokenRefreshTimer: NodeJS.Timeout | null = null;

  // Credentials state
  private static cachedCredentials: CachedCredentials | null = null;
  private static currentFetchPromise: Promise<any> | null = null;

  // Control flags
  private static authenticationFailed: boolean = false;
  private static currentTokenRefreshPromise: Promise<string | null> | null =
    null;
  private static onTokenRefresh: ((token: string) => void) | null = null;
  private static visibilityChangeListenerAdded: boolean = false;

  // Constants
  private static readonly CREDENTIAL_BUFFER_MINUTES = 5;
  private static readonly TOKEN_BUFFER_MINUTES = 2;

  private constructor() {}

  public static getInstance(): AuthenticationService {
    if (!AuthenticationService.instance) {
      AuthenticationService.instance = new AuthenticationService();
      AuthenticationService.setupVisibilityChangeListener();
    }
    return AuthenticationService.instance;
  }

  /**
   * Set ID token and reset all authentication state
   * This is the primary entry point for authentication
   */
  public static setIdToken(idToken: string | undefined) {
    // Clear existing state
    AuthenticationService.clearAllState();

    if (idToken) {
      // Set new token
      AuthenticationService.idToken = idToken;
      AuthenticationService.authenticationFailed = false;

      // Parse and schedule refresh
      AuthenticationService.parseTokenAndScheduleRefresh(idToken);
    }
  }

  /**
   * Set callback for token refresh events
   */
  public static setOnTokenRefresh(callback: (token: string) => void) {
    AuthenticationService.onTokenRefresh = callback;
  }

  /**
   * Get current ID token
   */
  public static getIdToken() {
    return AuthenticationService.idToken;
  }

  /**
   * Clear all authentication state
   */
  private static clearAllState() {
    // Clear token state
    AuthenticationService.idToken = undefined;
    AuthenticationService.tokenInfo = null;
    AuthenticationService.authenticationFailed = false;

    // Clear credentials
    AuthenticationService.cachedCredentials = null;
    AuthenticationService.currentFetchPromise = null;

    // Clear refresh state
    AuthenticationService.currentTokenRefreshPromise = null;
    if (AuthenticationService.tokenRefreshTimer) {
      clearTimeout(AuthenticationService.tokenRefreshTimer);
      AuthenticationService.tokenRefreshTimer = null;
    }
  }

  /**
   * Parse token and schedule automatic refresh
   */
  private static parseTokenAndScheduleRefresh(token: string) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiration = new Date(payload.exp * 1000);

      AuthenticationService.tokenInfo = {
        token,
        expiration,
      };

      // Schedule refresh before expiration
      const now = new Date();
      const refreshTime = new Date(
        expiration.getTime() -
          AuthenticationService.TOKEN_BUFFER_MINUTES * 60 * 1000
      );
      const delay = Math.max(0, refreshTime.getTime() - now.getTime());

      console.info(
        `Token expires at ${expiration.toISOString()}, will refresh at ${refreshTime.toISOString()}`
      );

      AuthenticationService.tokenRefreshTimer = setTimeout(() => {
        AuthenticationService.refreshToken();
      }, delay);
    } catch (error) {
      console.error("Failed to parse token expiration:", error);
    }
  }

  /**
   * Refresh token - only called when needed
   */
  private static async refreshToken(): Promise<string | null> {
    // Prevent multiple simultaneous refreshes
    if (AuthenticationService.currentTokenRefreshPromise) {
      return await AuthenticationService.currentTokenRefreshPromise;
    }

    AuthenticationService.currentTokenRefreshPromise =
      AuthenticationService.performTokenRefresh();
    const result = await AuthenticationService.currentTokenRefreshPromise;
    AuthenticationService.currentTokenRefreshPromise = null;
    return result;
  }

  /**
   * Perform actual token refresh
   */
  private static async performTokenRefresh(): Promise<string | null> {
    try {
      console.info("Refreshing token...");
      const session = await fetchAuthSession({ forceRefresh: true });
      const newToken = session.tokens?.idToken?.toString();

      if (newToken) {
        // Update token and reschedule
        AuthenticationService.idToken = newToken;
        AuthenticationService.parseTokenAndScheduleRefresh(newToken);

        // Notify listeners
        if (AuthenticationService.onTokenRefresh) {
          AuthenticationService.onTokenRefresh(newToken);
        }

        console.info("Token refreshed successfully");
        return newToken;
      } else {
        console.warn("No new token received from refresh");
        return null;
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      AuthenticationService.authenticationFailed = true;
      return null;
    }
  }

  /**
   * Setup visibility change listener for token refresh
   */
  private static setupVisibilityChangeListener() {
    if (AuthenticationService.visibilityChangeListenerAdded) {
      return;
    }

    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.info(
          "Page became visible, checking if token refresh is needed"
        );

        // Only refresh if no refresh is in progress and token is near expiry
        if (!AuthenticationService.currentTokenRefreshPromise) {
          const tokenInfo = AuthenticationService.tokenInfo;
          const now = new Date();
          const bufferMs =
            AuthenticationService.TOKEN_BUFFER_MINUTES * 60 * 1000;
          const isExpiredOrNear =
            !tokenInfo ||
            tokenInfo.expiration.getTime() - now.getTime() <= bufferMs;

          if (isExpiredOrNear) {
            console.info(
              "Token expired or near expiry on visibility change, refreshing"
            );
            try {
              await AuthenticationService.refreshToken();
            } catch (error) {
              console.warn("Token refresh failed on visibility change", error);
            }
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    AuthenticationService.visibilityChangeListenerAdded = true;
    console.info("Visibility change listener added to AuthenticationService");
  }

  /**
   * Check if credentials are expired
   */
  private isCredentialsExpired(expiration: Date): boolean {
    const bufferTime = new Date();
    bufferTime.setMinutes(
      bufferTime.getMinutes() + AuthenticationService.CREDENTIAL_BUFFER_MINUTES
    );
    return expiration <= bufferTime;
  }

  /**
   * Check if response indicates authentication failure
   */
  private async isAuthenticationError(response: Response): Promise<boolean> {
    // Check for common authentication error status codes
    if (response.status === 401 || response.status === 403) {
      return true;
    }

    // Check for GraphQL errors in the response body
    try {
      const responseText = await response.text();
      const responseData = JSON.parse(responseText);

      if (responseData.errors && Array.isArray(responseData.errors)) {
        const authErrorMessages = [
          "User not found",
          "Unauthorized",
          "Forbidden",
          "Authentication failed",
          "Invalid token",
          "Token expired",
        ];

        for (const error of responseData.errors) {
          if (
            error.message &&
            authErrorMessages.some((authMsg) =>
              error.message.toLowerCase().includes(authMsg.toLowerCase())
            )
          ) {
            return true;
          }
        }
      }
    } catch (error) {
      // If we can't parse the response as JSON, it's not a GraphQL error
      console.debug(
        "Could not parse response as JSON for error checking:",
        error
      );
    }

    return false;
  }

  /**
   * Fetch AWS credentials for API calls
   */
  private async fetchCredentials(): Promise<any> {
    console.info("Fetching new credentials");

    const userPoolProvider = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
    const idToken = AuthenticationService.idToken;

    if (!idToken) {
      throw new Error("No ID token available");
    }

    // Create a Cognito Identity Client
    const client = new CognitoIdentityClient({ region });

    // First, get the IdentityId using the logins map
    const getIdCommand = new GetIdCommand({
      IdentityPoolId: cognitoIdentityPoolId,
      Logins: {
        [userPoolProvider]: idToken,
      },
    });
    const identityResponse = await client.send(getIdCommand);

    // Then, get the temporary AWS credentials
    const getCredsCommand = new GetCredentialsForIdentityCommand({
      IdentityId: identityResponse.IdentityId,
      Logins: {
        [userPoolProvider]: idToken,
      },
    });
    const credsResponse = await client.send(getCredsCommand);

    const credentials = {
      accessKeyId: credsResponse.Credentials?.AccessKeyId,
      secretAccessKey: credsResponse.Credentials?.SecretKey,
      sessionToken: credsResponse.Credentials?.SessionToken,
      expiration: credsResponse.Credentials?.Expiration,
    };

    // Cache the credentials with their expiration time
    if (credentials.expiration) {
      AuthenticationService.cachedCredentials = {
        credentials,
        expiration: credentials.expiration,
      };
      console.info("Credentials cached until", credentials.expiration);
    } else {
      console.warn("Credentials returned without expiration, not caching");
    }

    return credentials;
  }

  /**
   * Get AWS credentials (cached or fresh)
   */
  getCredentials = async () => {
    if (!AuthenticationService.idToken) {
      console.info("No ID token");
      return {};
    }

    // Check if we have cached credentials that are still valid
    if (
      AuthenticationService.cachedCredentials &&
      !this.isCredentialsExpired(
        AuthenticationService.cachedCredentials.expiration
      )
    ) {
      console.info("Using cached credentials");
      return AuthenticationService.cachedCredentials.credentials;
    }

    // If there's already a fetch in progress, wait for it
    if (AuthenticationService.currentFetchPromise) {
      console.info("Credentials fetch already in progress, waiting...");
      try {
        return await AuthenticationService.currentFetchPromise;
      } finally {
        // Always clear to avoid getting stuck in a perpetual "in progress" state
        AuthenticationService.currentFetchPromise = null;
      }
    }

    // Start a new fetch and store the promise
    AuthenticationService.currentFetchPromise = this.fetchCredentials();
    try {
      return await AuthenticationService.currentFetchPromise;
    } finally {
      // Ensure we clear the promise whether it succeeds or fails
      AuthenticationService.currentFetchPromise = null;
    }
  };

  /**
   * Make authenticated GraphQL requests with proper error handling
   */
  awsGraphqlFetch = async (
    uri: RequestInfo | URL,
    init?: RequestInit | undefined,
    retryCount: number = 0
  ): Promise<Response> => {
    const MAX_RETRIES = 2;

    // If authentication has previously failed, don't execute until setIdToken is called
    if (AuthenticationService.authenticationFailed) {
      console.info(
        "awsGraphqlFetch: prior auth failure; waiting for valid token"
      );
      return new Response(
        JSON.stringify({
          data: null,
          errors: [{ message: "Authentication missing" }],
        }),
        { status: 200, headers: { "Content-Type": "application/json" } }
      );
    }

    // Ensure we have a valid token
    if (!AuthenticationService.idToken) {
      console.warn("awsGraphqlFetch: no ID token available");
      return new Response(
        JSON.stringify({
          data: null,
          errors: [{ message: "Authentication missing" }],
        }),
        { status: 200, headers: { "Content-Type": "application/json" } }
      );
    }

    // Check if token needs refresh
    const tokenInfo = AuthenticationService.tokenInfo;
    const now = new Date();
    const bufferMs = AuthenticationService.TOKEN_BUFFER_MINUTES * 60 * 1000;
    const isExpiredOrNear =
      !tokenInfo || tokenInfo.expiration.getTime() - now.getTime() <= bufferMs;

    if (isExpiredOrNear) {
      console.info("Token expired or near expiry, refreshing before request");
      try {
        await AuthenticationService.refreshToken();
      } catch (error) {
        console.warn("Token refresh failed before request", error);
        return new Response(
          JSON.stringify({
            data: null,
            errors: [{ message: "Authentication failed" }],
          }),
          { status: 200, headers: { "Content-Type": "application/json" } }
        );
      }
    }

    // Build request
    const {
      backendGateway: { host, path, graphqlPath },
      region,
    } = apiConfig;

    const request = {
      host,
      method: "POST",
      path: `${path}${graphqlPath}`,
      region,
      service: "execute-api",
      body: init?.body,
      headers: {
        "X-Atomic-IdToken": AuthenticationService.getIdToken() || "",
        ...init?.headers,
      },
    };

    const credentials = await this.getCredentials();
    const signedRequest = aws4.sign(request, {
      ...credentials,
    });

    // Clean up headers
    if (signedRequest?.headers && signedRequest?.headers["Host"]) {
      delete signedRequest.headers["Host"];
    }
    if (signedRequest?.headers && signedRequest?.headers["Content-Length"]) {
      delete signedRequest.headers["Content-Length"];
    }

    try {
      // Parse body to get GraphQL operation info
      let operationName = "unknown";
      try {
        if (signedRequest.body) {
          const body = typeof signedRequest.body === "string" 
            ? JSON.parse(signedRequest.body) 
            : signedRequest.body;
          operationName = body.operationName || body.query?.match(/mutation\s+(\w+)|query\s+(\w+)/)?.[1] || "unknown";
        }
      } catch (e) {
        // Body parsing failed, continue without operation info
      }

      console.log("[awsGraphqlFetch] Making request", {
        uri,
        method: signedRequest.method,
        operationName,
        headers: {
          "X-Atomic-CustomerId": signedRequest.headers?.["X-Atomic-CustomerId"] || signedRequest.headers?.["x-atomic-customerid"],
          "X-Atomic-IdToken": signedRequest.headers?.["X-Atomic-IdToken"] ? "present" : "missing",
          "X-Amz-Date": signedRequest.headers?.["X-Amz-Date"],
          Authorization: signedRequest.headers?.Authorization ? "present" : "missing",
        },
        timestamp: new Date().toISOString(),
      });
      const response = await fetch(uri, signedRequest as RequestInit);
      console.log("[awsGraphqlFetch] Response received", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        operationName,
        headers: {
          "access-control-allow-origin": response.headers.get("access-control-allow-origin"),
          "content-type": response.headers.get("content-type"),
        },
        timestamp: new Date().toISOString(),
      });

      // Check if the response indicates an authentication failure
      const responseClone = response.clone();
      if (await this.isAuthenticationError(responseClone)) {
        console.warn(
          `awsGraphqlFetch: authentication failed (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`
        );

        // If we haven't exceeded max retries, try to refresh token and retry
        if (retryCount < MAX_RETRIES) {
          console.info("Attempting to refresh token and retry...");
          const newToken = await AuthenticationService.refreshToken();

          if (newToken) {
            // Clear cached credentials to force refresh
            AuthenticationService.cachedCredentials = null;
            AuthenticationService.currentFetchPromise = null;

            // Retry the request with the new token
            return await this.awsGraphqlFetch(uri, init, retryCount + 1);
          } else {
            console.error(
              "Token refresh failed, marking authentication as failed"
            );
            AuthenticationService.authenticationFailed = true;
          }
        } else {
          console.error(
            "Max retries exceeded, marking authentication as failed"
          );
          AuthenticationService.authenticationFailed = true;
        }
      }

      return response;
    } catch (error) {
      console.error(
        `awsGraphqlFetch: fetch failed (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`,
        error
      );

      // For network errors, return a graceful response instead of throwing
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        console.warn(
          "Network error detected, returning graceful offline response"
        );
        return new Response(
          JSON.stringify({
            data: null,
            errors: [
              {
                message:
                  "Network unavailable. Please check your connection and try again.",
              },
            ],
          }),
          { status: 200, headers: { "Content-Type": "application/json" } }
        );
      }

      // Only mark as authentication failed if we've exhausted retries and it's not a network error
      if (retryCount >= MAX_RETRIES) {
        AuthenticationService.authenticationFailed = true;
      }

      throw error;
    }
  };
}
